import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, BellRing, BellOff } from "lucide-react";
import { toast } from "sonner";
import { http } from "../lib/api";
import { useRealtime } from "../context/RealtimeContext";
import { subscribePush, syncPush } from "../lib/push";
import { useAuth } from "../context/AuthContext";
import { QuietHours } from "./QuietHours";
import { NotifJournal } from "./NotifJournal";

const supported = () => "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = () => window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;

const usePush = () => {
  const [state, setState] = useState("off");
  useEffect(() => {
    if (!supported()) { setState("unsupported"); return; }
    if (Notification.permission === "denied") { setState("denied"); return; }
    navigator.serviceWorker.ready.then((r) => r.pushManager.getSubscription()).then((s) => setState(s ? "on" : "off")).catch(() => {});
    syncPush("/nav/push-key", "/nav/push-subscribe").catch(() => {});
  }, []);
  const enable = async () => {
    try {
      if ((await Notification.requestPermission()) !== "granted") { setState("denied"); return; }
      await subscribePush("/nav/push-key", "/nav/push-subscribe");
      setState("on"); await http.post("/nav/push-test").catch(() => {});
      toast.success("Notifications activées sur cet appareil");
    } catch (e) { toast.error("Activation impossible : " + (e?.message || "erreur")); }
  };
  const disable = async () => {
    const sub = await (await navigator.serviceWorker.ready).pushManager.getSubscription();
    if (sub) { await http.post("/nav/push-unsubscribe", { endpoint: sub.endpoint }).catch(() => {}); await sub.unsubscribe(); }
    setState("off");
  };
  return { state, enable, disable };
};

const PushRow = ({ push }) => {
  if (push.state === "unsupported") return <p className="text-xs text-slate-500" data-testid="push-unsupported">{isIos() && !standalone() ? "Sur iPhone, installez d'abord l'application (Partager → Sur l'écran d'accueil), puis ouvrez-la pour activer les alertes." : "Ce navigateur ne gère pas les alertes."}</p>;
  if (push.state === "denied") return <p className="text-xs text-rose-600" data-testid="push-denied">Alertes bloquées : autorisez les notifications pour XALTIX dans les réglages du téléphone ou du navigateur.</p>;
  return push.state === "on"
    ? <button onClick={push.disable} data-testid="push-disable-btn" className="w-full inline-flex items-center justify-center gap-1.5 rounded-md border border-slate-200 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50"><BellOff className="h-3.5 w-3.5" /> Désactiver les alertes sur cet appareil</button>
    : <button onClick={push.enable} data-testid="push-enable-btn" className="w-full inline-flex items-center justify-center gap-1.5 rounded-md bg-[#0B2A7A] py-2 text-xs font-semibold text-white hover:bg-[#0B2A7A]/90"><BellRing className="h-3.5 w-3.5" /> Activer les alertes sur cet appareil</button>;
};

export const NotificationBell = () => {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState({ items: [], unread: 0 });
  const push = usePush();
  const { lastEvent } = useRealtime();
  const nav = useNavigate();
  const ref = useRef(null);
  const { user } = useAuth();
  const [journal, setJournal] = useState(false);
  const load = () => http.get("/nav/notifications").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); const iv = setInterval(load, 60000); return () => clearInterval(iv); }, []);
  useEffect(() => { if (lastEvent?.event === "user_notification") load(); }, [lastEvent]);
  useEffect(() => { const f = (e) => ref.current && !ref.current.contains(e.target) && !e.target.closest?.("[data-sonner-toaster]") && setOpen(false); document.addEventListener("mousedown", f); return () => document.removeEventListener("mousedown", f); }, []);
  const toggle = () => { const o = !open; setOpen(o); if (o && data.unread) http.post("/nav/notifications/read-all").then(() => setData((d) => ({ ...d, unread: 0 }))).catch(() => {}); };
  return (
    <div className="relative" ref={ref}>
      <button onClick={toggle} data-testid="notif-bell-btn" aria-label="Notifications" className="relative p-2 rounded-full border border-transparent text-slate-600 hover:bg-slate-100">
        <Bell className="h-5 w-5" />
        {(data.unread > 0 || push.state === "off") && <span data-testid="notif-bell-count" className={`absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold grid place-items-center ring-2 ring-white ${data.unread ? "bg-rose-600 text-white" : "bg-amber-400 text-[#0B2A7A]"}`}>{data.unread ? (data.unread > 99 ? "99+" : data.unread) : "!"}</span>}
      </button>
      {open && (
        <div data-testid="notif-panel" className="fixed sm:absolute right-2 sm:right-0 left-2 sm:left-auto top-14 sm:top-11 z-[70] sm:w-96 bg-white border border-slate-200 rounded-xl shadow-2xl overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 space-y-2">
            <div className="flex items-center justify-between"><b className="font-display text-sm text-slate-900">Notifications</b>
              {user?.role === "admin" && <button onClick={() => { setOpen(false); setJournal(true); }} className="text-xs font-semibold text-[#0B2A7A] hover:underline" data-testid="notif-journal-btn">Journal des alertes</button>}</div>
            <PushRow push={push} />
            <QuietHours base="/nav" />
          </div>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100" data-testid="notif-list">
            {data.items.length === 0 && <p className="p-4 text-sm text-slate-500" data-testid="notif-empty">Aucune notification pour le moment.</p>}
            {data.items.map((n) => (
              <button key={n.id} data-testid={`notif-item-${n.id}`} onClick={() => { setOpen(false); if (n.url && n.url !== "/") nav(n.url); }} className={`block w-full text-left px-4 py-3 hover:bg-slate-50 ${n.urgent ? "border-l-4 border-rose-500" : ""} ${n.read ? "" : "bg-amber-50/60"}`}>
                <div className={`text-sm font-semibold line-clamp-1 ${n.urgent ? "text-rose-700" : "text-slate-800"}`}>{n.title}</div>
                <div className="text-xs text-slate-500 line-clamp-2">{n.body}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{new Date(n.created_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}{n.mode === "silent" ? " · reçue en silencieux" : ""}</div>
              </button>
            ))}
          </div>
        </div>
      )}
      {journal && <NotifJournal onClose={() => setJournal(false)} />}
    </div>
  );
};
